import { ContentType } from '@creditsights/api-ui-web';
import classNames from 'classnames/bind';
import isEmpty from 'lodash/isEmpty';
import React, { ReactElement } from 'react';
import AnalystsWidget from '../../../components/widgets/Analysts';
import { AskAnalystModalSubject } from '../../../components/widgets/Analysts/AnalystsWidget';
import MorningCommentLinkWidget from '../../../components/widgets/MorningCommentLink';
import MultipartDocumentsWidget from '../../../components/widgets/MultipartDocuments';
import RelatedCompaniesWidget from '../../../components/widgets/RelatedCompanies';
import RelatedDocumentsWidget from '../../../components/widgets/RelatedDocuments';
import RelatedTopicsWidget from '../../../components/widgets/RelatedTopics';
import useFetchUserPermissions from '../../../hooks/permissions/useFetchUserPermissions';
import { Component, Region, Tag } from '../../../models';
import { DEFAULT_ARTICLE_ORDER_CONFIG, WIDGET_ID } from '../Article.constants';
import styles from './ArticleRightContainer.module.scss';

const cx = classNames.bind(styles);

interface ArticleRightContainerProps {
  relatedDocuments: Component[];
  multipartTag?: string;
  analysts: string[];
  isTabletPortrait: boolean;
  focusCompanyTags: string[];
  secondaryCompanyTags: string[];
  relatedTopics: Tag[];
  morningCommentRegion: Region | null;
  isArticlePreviewOpen: boolean;
  contentType?: ContentType;
  askAnalystModalSubject?: AskAnalystModalSubject;
  setIsAnalystWidgetLoading: (value: boolean) => void;
}

const ArticleRightContainer = ({
  relatedDocuments,
  multipartTag,
  isTabletPortrait,
  focusCompanyTags,
  secondaryCompanyTags,
  analysts,
  relatedTopics,
  morningCommentRegion,
  isArticlePreviewOpen,
  contentType,
  askAnalystModalSubject,
  setIsAnalystWidgetLoading,
}: ArticleRightContainerProps): ReactElement => {
  const { data: userPermissions } = useFetchUserPermissions();
  const { userSubscription } = userPermissions!;
  const isCovenantsArticle = contentType === ContentType.Covenants;

  const renderMorningCommentWidget = (widgetId: WIDGET_ID) => {
    if (!morningCommentRegion) {
      return null;
    }
    return <MorningCommentLinkWidget key={widgetId} region={morningCommentRegion} />;
  };

  const renderAnalystsWidget = (widgetId: WIDGET_ID) => {
    if (isTabletPortrait) {
      return null;
    }
    return (
      <AnalystsWidget
        key={widgetId}
        analysts={isCovenantsArticle ? analysts.slice(0, 1) : analysts}
        askAnalystModalSubject={askAnalystModalSubject}
        setIsArticleAnalystWidgetLoading={setIsAnalystWidgetLoading}
      />
    );
  };

  const renderRelatedDocumentsWidget = (widgetId: WIDGET_ID) => {
    if (isEmpty(relatedDocuments) || contentType !== ContentType.Covenants) {
      return null;
    }
    return <RelatedDocumentsWidget key={widgetId} relatedDocuments={relatedDocuments} />;
  };

  const renderMultiPartDocumentsWidget = (widgetId: WIDGET_ID) => {
    if (!multipartTag || contentType !== ContentType.Research) {
      return null;
    }
    return <MultipartDocumentsWidget key={widgetId} tag={multipartTag} />;
  };

  const renderRelatedCompaniesWidget = (widgetId: WIDGET_ID) => {
    if (isEmpty(focusCompanyTags) && isEmpty(secondaryCompanyTags)) {
      return null;
    }
    return (
      <RelatedCompaniesWidget
        key={widgetId}
        focusCompanyTags={focusCompanyTags}
        secondaryCompanyTags={secondaryCompanyTags}
        contentType={contentType}
      />
    );
  };

  const renderWidget = (widgetId: WIDGET_ID) => {
    switch (widgetId) {
      case WIDGET_ID.MORNING_COMMENT_LINK:
        return renderMorningCommentWidget(widgetId);
      case WIDGET_ID.ANALYSTS:
        return renderAnalystsWidget(widgetId);
      case WIDGET_ID.RELATED_TOPICS:
        return <RelatedTopicsWidget key={widgetId} relatedTopics={relatedTopics} contentType={contentType} />;
      case WIDGET_ID.RELATED_DOCUMENTS:
        return renderRelatedDocumentsWidget(widgetId);
      case WIDGET_ID.MULTIPART_DOCUMENTS:
        return renderMultiPartDocumentsWidget(widgetId);
      case WIDGET_ID.RELATED_COMPANIES:
        return renderRelatedCompaniesWidget(widgetId);
      default: {
        return null;
      }
    }
  };

  const renderWidgets = () => {
    const widgetIds = DEFAULT_ARTICLE_ORDER_CONFIG[userSubscription];
    return widgetIds.map((widgetId) => renderWidget(widgetId));
  };

  return (
    <div
      className={cx('body', { 'article-preview-open': isArticlePreviewOpen })}
      id="article-right-container"
      data-testid="article-right-container"
    >
      {renderWidgets()}
    </div>
  );
};

export default ArticleRightContainer;
